import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage aria-hidden attribute on the root element for modal accessibility.
 * This ensures that when a modal is open, the background content is hidden from screen readers.
 *
 * @param isModalOpen - Boolean indicating whether the modal is currently open
 * @param rootElementSelector - CSS selector for the root element (default: '#root')
 */
const useModalAriaHidden = (isModalOpen: boolean, rootElementSelector: string = '#root') => {
  const previousAriaHiddenRef = useRef<string | null>(null);
  const modalCountRef = useRef<number>(0);

  useEffect(() => {
    const rootElement = document.querySelector(rootElementSelector) as HTMLElement;

    if (!rootElement) {
      console.warn(`useModalAriaHidden: Element with selector "${rootElementSelector}" not found`);
      return;
    }

    const setAriaHidden = () => {
      previousAriaHiddenRef.current = rootElement.getAttribute('aria-hidden');
      rootElement.setAttribute('aria-hidden', 'true');
    };

    const restoreAriaHidden = () => {
      if (previousAriaHiddenRef.current === null) {
        rootElement.removeAttribute('aria-hidden');
      } else {
        rootElement.setAttribute('aria-hidden', previousAriaHiddenRef.current);
      }
      previousAriaHiddenRef.current = null;
    };

    if (isModalOpen) {
      modalCountRef.current += 1;

      if (modalCountRef.current === 1) {
        setAriaHidden();
      }
    } else {
      modalCountRef.current = Math.max(0, modalCountRef.current - 1);

      if (modalCountRef.current === 0) {
        restoreAriaHidden();
      }
    }

    return () => {
      if (isModalOpen && modalCountRef.current > 0) {
        modalCountRef.current -= 1;

        if (modalCountRef.current === 0) {
          restoreAriaHidden();
        }
      }
    };
  }, [isModalOpen, rootElementSelector]);
};

export default useModalAriaHidden;
